import React, { useState, useContext, useMemo } from "react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import DetailHeader from "../components/DetailHeader";
import UserForm from "./UserForm";
import "./UserDetail.css";

export default function UserDetail({ user, onBack, onUpdate }) {
  const { roles, companies, users } = useContext(FieldConfigurationContext);
  const [isEditing, setIsEditing] = useState(false);

  const configureUsersData = companies?.configureUsersData || {};
  const isMultiCompany = Object.keys(configureUsersData).length > 1;

  // Build per-company card data for the right panel
  const perCompanyCards = useMemo(() => {
    return Object.entries(configureUsersData)
      .map(([uuid, name]) => {
        const companyIdx = user.companyUuids?.indexOf(uuid) ?? -1;
        const isAssigned = companyIdx >= 0;

        // In multi-company mode, skip companies the user isn't assigned to
        if (!isAssigned && isMultiCompany) return null;

        const companyUserUuid = isAssigned
          ? user.uuids?.[companyIdx]
          : user.uuids?.[0] || user.uuid;

        const record = companyUserUuid
          ? users.data.find((u) => u.uuid === companyUserUuid)
          : null;

        const roleUuid = record?.roleUuid || record?.role;
        const role = roles.data.find((r) => r.uuid === roleUuid);
        const code = companies.codesMap?.[uuid];

        return {
          uuid,
          displayName: code ? `${name} (${code})` : name,
          roleName: role?.name || "—",
          isActive: record?.active !== false,
          employee: record?.employee || record?.code || "",
          groups: record?.groups || [],
        };
      })
      .filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companies.configureUsersData,
    companies.codesMap,
    user,
    users.data,
    roles.data,
  ]);

  // If editing, show the UserForm component
  if (isEditing) {
    return (
      <UserForm
        user={user}
        onSave={(updatedUser) => {
          setIsEditing(false);
          onUpdate(updatedUser);
        }}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className="user-detail">
      <DetailHeader
        title={`${user.firstName} ${user.lastName}`}
        onBack={onBack}
        onEdit={() => setIsEditing(true)}
        isEditing={false}
        canEdit={true}
        showBack={true}
      />

      <div className="user-detail__two-col">
        {/* Left Panel */}
        <div className="user-detail__left">
          <div className="user-detail__section user-detail__section--info">
            <h3 className="user-detail__section-title">Personal Information</h3>
            <div className="user-detail__fields">
              <div className="user-detail__name-row">
                <div className="user-detail__field">
                  <label className="user-detail__label">First Name</label>
                  <div className="user-detail__value">
                    {user.firstName || "—"}
                  </div>
                </div>
                <div className="user-detail__field">
                  <label className="user-detail__label">Last Name</label>
                  <div className="user-detail__value">
                    {user.lastName || "—"}
                  </div>
                </div>
              </div>

              <div className="user-detail__field">
                <label className="user-detail__label">Email</label>
                <div className="user-detail__value">{user.email || "—"}</div>
              </div>

              {user.mobileNumber && (
                <div className="user-detail__field">
                  <label className="user-detail__label">Mobile Number</label>
                  <div className="user-detail__value">{user.mobileNumber}</div>
                </div>
              )}

              {user.lastLogin && (
                <div className="user-detail__field">
                  <label className="user-detail__label">Last Login</label>
                  <div className="user-detail__value">
                    {new Date(user.lastLogin).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isMultiCompany && (
            <div className="user-detail__section">
              <h3 className="user-detail__section-title">Companies</h3>
              <div className="user-detail__company-badges">
                {Object.entries(configureUsersData).map(([uuid, name]) => {
                  const companyIdx = user.companyUuids?.indexOf(uuid) ?? -1;
                  const isAssigned = companyIdx >= 0;
                  const companyUserUuid = isAssigned
                    ? user.uuids?.[companyIdx]
                    : null;
                  const companyUserRecord = companyUserUuid
                    ? users.data.find((u) => u.uuid === companyUserUuid)
                    : null;
                  const isActive = companyUserRecord?.active !== false;
                  const badgeClass = isAssigned
                    ? isActive
                      ? " user-detail__company-badge--assigned"
                      : " user-detail__company-badge--assigned-inactive"
                    : "";
                  const code = companies.codesMap?.[uuid];
                  return (
                    <span
                      key={uuid}
                      className={`user-detail__company-badge${badgeClass}`}
                    >
                      {code != null ? `${name} (${code})` : name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div className="user-detail__right">
          <h3 className="user-detail__panel-title">
            Per-Company Configuration
          </h3>
          {perCompanyCards.length === 0 ? (
            <div className="user-detail__empty-state">
              No company assignments available.
            </div>
          ) : (
            perCompanyCards.map(
              ({ uuid, displayName, roleName, isActive, employee, groups }) => (
                <div key={uuid} className="user-detail__company-card">
                  <div className="user-detail__company-card-header">
                    <span className="user-detail__company-card-name">
                      {displayName}
                    </span>
                    <span
                      className={`user-status-badge ${
                        isActive
                          ? "user-status-badge--active"
                          : "user-status-badge--inactive"
                      }`}
                    >
                      {isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="user-detail__company-card-body">
                    {employee && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">
                          Employee ID
                        </label>
                        <div className="user-detail__value">{employee}</div>
                      </div>
                    )}
                    <div className="user-detail__field">
                      <label className="user-detail__label">Role</label>
                      <div className="user-detail__value">{roleName}</div>
                    </div>
                    {groups.length > 0 && (
                      <div className="user-detail__field">
                        <label className="user-detail__label">
                          Access Groups
                        </label>
                        <div className="user-detail__groups">
                          {groups.map((g, i) => (
                            <span
                              key={g.uuid || i}
                              className="user-detail__group-tag"
                            >
                              {g.name || g.uuid}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>
    </div>
  );
}
